/**
 * Breadcrumbs de debug para el freeze del file picker.
 * - console.warn inmediato (con timestamp)
 * - localStorage (sync) para leer tras forzar cierre de pestaña
 *
 * En consola (tras reabrir la pestaña congelada):
 *   __gastosPickerDump()   → breadcrumbs + heartbeat/tick/longtasks/storm
 *   __gastosPickerClear()
 *
 * Interpretación tras un freeze:
 * - Señal real = tick.STORM.detected (livelock de estabilización).
 * - detectChanges diferido ausente tras beforeDetectChanges = inanición de
 *   macrotasks por bucle de microtasks (no rAF: rAF ≤ ~60/s).
 * - endMs:null durante tormenta es casi tautológico (siempre estás "dentro"
 *   de un tick al matar la pestaña a 10k+ ticks/s).
 * - stacks [Zone.scheduleMicroTask] / [queueMicrotask] → origen del loop.
 * - Claves van namespaced por tabId; evidencia de freeze se copia a
 *   gastos-picker.frozen.* (solo se pisa con reason storm-*; F5 no la borra).
 * - cd.deferred.missing en heartbeat es macrotask: durante tormenta no corre;
 *   también se evalúa dentro de tick (síncrono).
 * - restore() de armStormCapture usa setTimeout: tampoco corre en tormenta;
 *   el restore real es al llenar maxStacks.
 */
const TAB_SESSION_KEY = 'gastos-picker-tab-id';
const FROZEN_PREFIX = 'gastos-picker.frozen.';

const MAX = 80;
const MAX_LONGTASKS = 20;
const MAX_SLOW_TICKS = 20;
const SLOW_TICK_MS = 50;
const DEFERRED_CD_WARN_MS = 500;

let tickSeq = 0;
let deferredCdPendingSince: number | null = null;
let deferredCdMissingReported = false;
let tabId = 'boot';
/** true tras tick.STORM.detected en esta pestaña (sobrevive al wrapper de tick). */
let stormEverDetected = false;

type KeyKind =
  | 'debug'
  | 'heartbeat'
  | 'tick'
  | 'storm'
  | 'longtasks'
  | 'slow-ticks'
  | 'prev-heartbeat'
  | 'prev-tick'
  | 'prev-storm'
  | 'prev-longtasks'
  | 'prev-slow-ticks'
  | 'prev-breadcrumbs';

function ensureTabId(): string {
  if (typeof sessionStorage === 'undefined') return 'ssr';
  try {
    let id = sessionStorage.getItem(TAB_SESSION_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(TAB_SESSION_KEY, id);
    }
    tabId = id;
    return id;
  } catch {
    tabId = 'anon';
    return tabId;
  }
}

function tabKey(kind: KeyKind): string {
  return `gastos-picker.${ensureTabId()}.${kind}`;
}

function frozenKey(kind: string): string {
  return `${FROZEN_PREFIX}${kind}`;
}

function stamp(): { t: string; ms: number } {
  return {
    t: new Date().toISOString(),
    ms: typeof performance !== 'undefined' ? Math.round(performance.now()) : 0
  };
}

function safeSet(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / private mode */
  }
}

function safeGet<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function sessionContext(): Record<string, unknown> {
  if (typeof document === 'undefined' || typeof location === 'undefined') {
    return { tabId: ensureTabId() };
  }
  return {
    tabId: ensureTabId(),
    path: location.pathname,
    canvas: document.querySelectorAll('canvas').length,
    tickSeq
  };
}

/** Copia el estado vivo de ESTA pestaña a claves frozen.* (compartidas).
 * No pisa evidencia de tormenta con snapshots “limpios” (pagehide/F5).
 */
function snapshotToFrozen(reason: string): void {
  try {
    const isStormReason = reason.startsWith('storm');
    if (!isStormReason) {
      const existing = localStorage.getItem(frozenKey('meta'));
      if (existing) {
        const meta = JSON.parse(existing) as { reason?: string } | null;
        if (meta?.reason && String(meta.reason).startsWith('storm')) {
          // Conservar freeze; un F5/navegación normal no debe borrar stacks.
          return;
        }
      }
    }

    const kinds: Array<[string, KeyKind]> = [
      ['heartbeat', 'heartbeat'],
      ['tick', 'tick'],
      ['storm', 'storm'],
      ['longtasks', 'longtasks'],
      ['slow-ticks', 'slow-ticks'],
      ['breadcrumbs', 'debug']
    ];
    const meta = {
      t: new Date().toISOString(),
      ms: stamp().ms,
      reason,
      tabId: ensureTabId(),
      ...sessionContext()
    };
    localStorage.setItem(frozenKey('meta'), JSON.stringify(meta));
    for (const [frozenKind, liveKind] of kinds) {
      const raw = localStorage.getItem(tabKey(liveKind));
      if (raw) localStorage.setItem(frozenKey(frozenKind), raw);
    }
  } catch {
    /* ignore */
  }
}

export function pickerDebug(step: string, detail?: unknown): void {
  const s = stamp();
  const entry = { t: s.t, ms: s.ms, step, detail: detail ?? null, tabId: ensureTabId() };

  if (step === 'ui.aplicarArchivo.beforeDetectChanges') {
    deferredCdPendingSince = s.ms;
    deferredCdMissingReported = false;
  }
  if (
    step === 'ui.aplicarArchivo.detectChanges' ||
    step === 'ui.aplicarArchivo.afterDetectChanges'
  ) {
    deferredCdPendingSince = null;
    deferredCdMissingReported = false;
  }

  try {
    console.warn('[gastos-picker]', s.t.slice(11, 23), s.ms, step, detail ?? '');
  } catch {
    /* ignore */
  }

  try {
    const raw = localStorage.getItem(tabKey('debug'));
    const prev: unknown[] = raw ? JSON.parse(raw) : [];
    const next = Array.isArray(prev) ? prev : [];
    next.push(entry);
    localStorage.setItem(tabKey('debug'), JSON.stringify(next.slice(-MAX)));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Llamar al ARRANCAR la app, ANTES de installPickerHeartbeat/installTickTrace.
 * Promueve evidencia frozen.* (de la pestaña que se congeló) a prev-* de esta
 * pestaña, sin mezclar heartbeats de otras pestañas vivas.
 */
export function preserveFreezeEvidence(): void {
  ensureTabId();
  try {
    const frozenMeta = localStorage.getItem(frozenKey('meta'));
    const frozenHb = localStorage.getItem(frozenKey('heartbeat'));
    const frozenTick = localStorage.getItem(frozenKey('tick'));
    const frozenStorm = localStorage.getItem(frozenKey('storm'));
    const frozenLt = localStorage.getItem(frozenKey('longtasks'));
    const frozenSt = localStorage.getItem(frozenKey('slow-ticks'));
    const frozenBc = localStorage.getItem(frozenKey('breadcrumbs'));

    if (frozenHb) localStorage.setItem(tabKey('prev-heartbeat'), frozenHb);
    if (frozenTick) localStorage.setItem(tabKey('prev-tick'), frozenTick);
    if (frozenStorm) localStorage.setItem(tabKey('prev-storm'), frozenStorm);
    if (frozenLt) localStorage.setItem(tabKey('prev-longtasks'), frozenLt);
    if (frozenSt) localStorage.setItem(tabKey('prev-slow-ticks'), frozenSt);
    if (frozenBc) localStorage.setItem(tabKey('prev-breadcrumbs'), frozenBc);

    // Si no hay frozen (primera visita), no inventar evidencia de otras tabs.
    const tickObj = frozenTick ? JSON.parse(frozenTick) : null;
    const metaObj = frozenMeta ? JSON.parse(frozenMeta) : null;
    pickerDebug('app.prevSession', {
      frozenMeta: metaObj,
      heartbeat: frozenHb ? JSON.parse(frozenHb) : null,
      tick: tickObj,
      tickNeverEnded: !!(tickObj && tickObj.endMs == null),
      hadStorm: !!frozenStorm,
      longtaskCount: frozenLt ? (JSON.parse(frozenLt) as unknown[])?.length ?? 0 : 0,
      slowTickCount: frozenSt ? (JSON.parse(frozenSt) as unknown[])?.length ?? 0 : 0,
      thisTabId: ensureTabId()
    });
  } catch {
    /* ignore */
  }
}

/**
 * Latido cada `intervalMs`: sobreescribe UNA clave de localStorage de ESTA pestaña.
 */
export function installPickerHeartbeat(intervalMs = 250): void {
  if (typeof window === 'undefined') return;
  const w = window as any;
  if (w.__gastosHeartbeatInstalled) return;
  w.__gastosHeartbeatInstalled = true;
  ensureTabId();

  // Solo snapshot en unload SI ya hubo tormenta. Un F5 normal no debe
  // pisar frozen.* de un freeze previo (y aunque lo intente, snapshotToFrozen
  // protege reason storm-*).
  const maybeSnapshotUnload = (reason: string) => {
    if (stormEverDetected) snapshotToFrozen(reason);
  };
  window.addEventListener('pagehide', () => maybeSnapshotUnload('pagehide-after-storm'));
  window.addEventListener('beforeunload', () => maybeSnapshotUnload('beforeunload-after-storm'));

  window.setInterval(() => {
    const s = stamp();
    try {
      localStorage.setItem(
        tabKey('heartbeat'),
        JSON.stringify({ ...s, ...sessionContext() })
      );
    } catch {
      /* ignore */
    }
    // Nota: durante tormenta este setInterval NO corre (inanición de macrotasks).
    // cd.deferred.missing también se evalúa en installTickTrace (path síncrono).
    checkDeferredCdMissing(s.ms);
  }, intervalMs);
}

function checkDeferredCdMissing(nowMs: number): void {
  if (
    deferredCdPendingSince != null &&
    !deferredCdMissingReported &&
    nowMs - deferredCdPendingSince > DEFERRED_CD_WARN_MS
  ) {
    deferredCdMissingReported = true;
    pickerDebug('cd.deferred.missing', {
      waitedMs: nowMs - deferredCdPendingSince,
      ...sessionContext()
    });
    // Solo útil si NO hay tormenta (si hay tormenta, macrotask ya estaba muerto).
    if (!stormEverDetected) snapshotToFrozen('cd.deferred.missing');
  }
}

/**
 * PerformanceObserver longtask: persiste bloqueos del main thread (>50ms).
 */
export function installLongTaskObserver(): void {
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') return;
  const w = window as any;
  if (w.__gastosLongTaskInstalled) return;
  w.__gastosLongTaskInstalled = true;

  try {
    const obs = new PerformanceObserver((list) => {
      const rows = safeGet<unknown[]>(tabKey('longtasks')) || [];
      for (const entry of list.getEntries()) {
        rows.push({
          t: new Date().toISOString(),
          ms: Math.round(performance.now()),
          name: entry.name,
          startTime: Math.round(entry.startTime),
          duration: Math.round(entry.duration),
          ...sessionContext()
        });
      }
      safeSet(tabKey('longtasks'), rows.slice(-MAX_LONGTASKS));
    });
    obs.observe({ type: 'longtask', buffered: true } as PerformanceObserverInit);
  } catch {
    /* Safari / navegadores sin longtask */
  }
}

/**
 * Captura stacks de quien agenda microtasks durante una tormenta.
 * Hook correcto: Zone.prototype.scheduleMicroTask + queueMicrotask.
 * Promise.then es secundario (await nativo ES2017+ no pasa por el prototipo).
 * rAF NO puede explicar 10k+ ticks/s; no es el driver del livelock.
 */
function armStormCapture(maxStacks = 8): void {
  const w = window as any;
  if (w.__gastosStormCapturing) return;
  w.__gastosStormCapturing = true;

  const stacks: string[] = [];
  const seen = new Set<string>();
  let done = false;

  const pushStack = (kind: string) => {
    if (done || stacks.length >= maxStacks) return;
    const raw = String(new Error(`capture:${kind}`).stack || '');
    const stack = `[${kind}]\n` + raw.split('\n').slice(2, 12).join('\n');
    const key = stack.slice(0, 260);
    if (!key || seen.has(key)) return;
    seen.add(key);
    stacks.push(stack);
    try {
      localStorage.setItem(
        tabKey('storm'),
        JSON.stringify({
          t: new Date().toISOString(),
          count: stacks.length,
          stacks,
          tabId: ensureTabId()
        })
      );
      snapshotToFrozen('storm-stack');
    } catch {
      /* ignore */
    }
    if (stacks.length >= maxStacks) {
      done = true;
      restore();
    }
  };

  // 1) Zone.scheduleMicroTask — canal principal de zone.js
  const ZoneRef = w.Zone as { prototype?: { scheduleMicroTask?: Function } } | undefined;
  let origSchedule: Function | null = null;
  if (ZoneRef?.prototype?.scheduleMicroTask && !(ZoneRef.prototype as any).__gastosCapturing) {
    origSchedule = ZoneRef.prototype.scheduleMicroTask;
    (ZoneRef.prototype as any).__gastosCapturing = true;
    ZoneRef.prototype.scheduleMicroTask = function (
      this: unknown,
      source: string,
      callback: Function,
      data?: unknown,
      customSchedule?: Function
    ) {
      pushStack(`Zone.scheduleMicroTask:${source || '?'}`);
      return (origSchedule as Function).call(this, source, callback, data, customSchedule);
    };
  }

  // 2) queueMicrotask nativo
  let origQmt: typeof queueMicrotask | null = null;
  if (typeof queueMicrotask === 'function' && !w.__gastosQmtCapturing) {
    w.__gastosQmtCapturing = true;
    origQmt = queueMicrotask.bind(window);
    w.queueMicrotask = function (cb: () => void) {
      pushStack('queueMicrotask');
      return (origQmt as typeof queueMicrotask)(cb);
    };
  }

  // 3) Promise.then — secundario (no ve await nativo)
  const proto = Promise.prototype as any;
  let origThen: any = null;
  if (!proto.__gastosCapturing) {
    proto.__gastosCapturing = true;
    origThen = proto.then;
    proto.then = function (this: Promise<unknown>, ...args: unknown[]) {
      pushStack('Promise.then');
      return (origThen as any).apply(this, args);
    };
  }

  function restore(): void {
    if (origSchedule && ZoneRef?.prototype) {
      ZoneRef.prototype.scheduleMicroTask = origSchedule as any;
      (ZoneRef.prototype as any).__gastosCapturing = false;
    }
    if (origQmt && w.__gastosQmtCapturing) {
      w.queueMicrotask = origQmt;
      w.__gastosQmtCapturing = false;
    }
    if (origThen && proto.__gastosCapturing) {
      proto.then = origThen;
      proto.__gastosCapturing = false;
    }
    w.__gastosStormCapturing = false;
  }

  window.setTimeout(() => {
    if (!done) {
      done = true;
      restore();
      snapshotToFrozen('storm-timeout');
    }
  }, 2500);
}

function pushSlowTick(entry: unknown): void {
  const rows = safeGet<unknown[]>(tabKey('slow-ticks')) || [];
  rows.push(entry);
  safeSet(tabKey('slow-ticks'), rows.slice(-MAX_SLOW_TICKS));
}

/**
 * Envuelve ApplicationRef.tick. Escribe tick con endMs=null ANTES de cada tick
 * (throttled en tormenta). La señal útil es STORM, no un endMs:null aislado.
 */
export function installTickTrace(appRef: { tick: () => void }): void {
  const anyRef = appRef as any;
  if (anyRef.__gastosTickTraced) return;
  anyRef.__gastosTickTraced = true;
  const originalTick = appRef.tick.bind(appRef);
  let bucketStart = 0;
  let bucketCount = 0;
  let stormReported = false;
  let lastTickPersistMs = 0;
  anyRef.tick = () => {
    tickSeq++;
    const seq = tickSeq;
    const start = stamp();
    // Detector de tormenta: >250 ticks en 500ms = livelock en curso.
    if (start.ms - bucketStart > 500) {
      bucketStart = start.ms;
      bucketCount = 0;
    }
    bucketCount++;
    if (bucketCount > 250 && !stormReported) {
      stormReported = true;
      stormEverDetected = true;
      armStormCapture();
      pickerDebug('tick.STORM.detected', { seq, ms: start.ms, ...sessionContext() });
      snapshotToFrozen('storm-detected');
    }
    // Path síncrono: si el setTimeout(0) de detectChanges nunca corre por
    // inanición de macrotasks, lo detectamos aquí (el heartbeat no puede).
    checkDeferredCdMissing(start.ms);
    // Durante tormenta, no spamear localStorage en cada tick.
    const persistEvery = stormReported ? 50 : 1;
    const shouldPersistStart =
      seq % persistEvery === 0 || start.ms - lastTickPersistMs > 100;
    if (shouldPersistStart) {
      lastTickPersistMs = start.ms;
      try {
        localStorage.setItem(
          tabKey('tick'),
          JSON.stringify({
            seq,
            startT: start.t,
            startMs: start.ms,
            endMs: null,
            ...sessionContext()
          })
        );
      } catch {
        /* ignore */
      }
    }
    try {
      originalTick();
    } finally {
      const end = stamp();
      const durMs = end.ms - start.ms;
      if (shouldPersistStart || durMs >= SLOW_TICK_MS) {
        try {
          localStorage.setItem(
            tabKey('tick'),
            JSON.stringify({
              seq,
              startT: start.t,
              startMs: start.ms,
              endMs: end.ms,
              durMs,
              ...sessionContext()
            })
          );
        } catch {
          /* ignore */
        }
      }
      if (durMs >= SLOW_TICK_MS) {
        pushSlowTick({
          seq,
          startT: start.t,
          startMs: start.ms,
          durMs,
          ...sessionContext()
        });
      }
    }
  };
}

/**
 * Prefija TODOS los console.log/info/warn/error con hora + performance.now().
 */
export function installConsoleTimestamps(): void {
  if (typeof window === 'undefined') return;
  const w = window as any;
  if (w.__gastosConsoleTsInstalled) return;
  w.__gastosConsoleTsInstalled = true;
  (['log', 'info', 'warn', 'error'] as const).forEach((m) => {
    const orig = console[m].bind(console);
    (console as any)[m] = (...args: unknown[]) => {
      const s = stamp();
      orig(`[${s.t.slice(11, 23)}|${s.ms}ms]`, ...args);
    };
  });
}

export function pickerDebugDump(): unknown[] {
  try {
    // Preferir breadcrumbs frozen (sesión del freeze) si existen.
    const frozen = localStorage.getItem(frozenKey('breadcrumbs'));
    if (frozen) return JSON.parse(frozen);
    return JSON.parse(localStorage.getItem(tabKey('debug')) || '[]');
  } catch {
    return [];
  }
}

export function pickerDebugClear(): void {
  try {
    const id = ensureTabId();
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith(`gastos-picker.${id}.`) || k.startsWith(FROZEN_PREFIX)) {
        toRemove.push(k);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
    // Limpiar también el esquema legacy sin tabId
    [
      'gastos-picker-debug',
      'gastos-picker-heartbeat',
      'gastos-picker-tick',
      'gastos-picker-storm',
      'gastos-picker-longtasks',
      'gastos-picker-slow-ticks',
      'gastos-picker-prev-heartbeat',
      'gastos-picker-prev-tick',
      'gastos-picker-prev-storm',
      'gastos-picker-prev-longtasks',
      'gastos-picker-prev-slow-ticks',
      'gastos-picker-prev-breadcrumbs'
    ].forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

/** Expone helpers en window para inspección manual. */
export function installPickerDebugGlobals(): void {
  if (typeof window === 'undefined') return;
  const w = window as any;
  ensureTabId();
  w.__gastosPickerDump = () => {
    const rows = pickerDebugDump();
    console.table(rows);
    let heartbeat: unknown = null;
    let tick: any = null;
    let prevHeartbeat: unknown = null;
    let prevTick: any = null;
    let storm: any = null;
    let prevStorm: any = null;
    let longtasks: unknown = null;
    let prevLongtasks: unknown = null;
    let slowTicks: unknown = null;
    let prevSlowTicks: unknown = null;
    let frozenMeta: unknown = null;
    try {
      heartbeat = JSON.parse(localStorage.getItem(tabKey('heartbeat')) || 'null');
      tick = JSON.parse(localStorage.getItem(tabKey('tick')) || 'null');
      prevHeartbeat = JSON.parse(localStorage.getItem(tabKey('prev-heartbeat')) || 'null');
      prevTick = JSON.parse(localStorage.getItem(tabKey('prev-tick')) || 'null');
      storm = JSON.parse(localStorage.getItem(tabKey('storm')) || 'null');
      prevStorm = JSON.parse(localStorage.getItem(tabKey('prev-storm')) || 'null');
      longtasks = JSON.parse(localStorage.getItem(tabKey('longtasks')) || 'null');
      prevLongtasks = JSON.parse(localStorage.getItem(tabKey('prev-longtasks')) || 'null');
      slowTicks = JSON.parse(localStorage.getItem(tabKey('slow-ticks')) || 'null');
      prevSlowTicks = JSON.parse(localStorage.getItem(tabKey('prev-slow-ticks')) || 'null');
      frozenMeta = JSON.parse(localStorage.getItem(frozenKey('meta')) || 'null');
      // Si no hay prev-* en esta tab, leer frozen directamente
      if (!prevStorm) prevStorm = JSON.parse(localStorage.getItem(frozenKey('storm')) || 'null');
      if (!prevTick) prevTick = JSON.parse(localStorage.getItem(frozenKey('tick')) || 'null');
      if (!prevHeartbeat) {
        prevHeartbeat = JSON.parse(localStorage.getItem(frozenKey('heartbeat')) || 'null');
      }
      if (!prevLongtasks) {
        prevLongtasks = JSON.parse(localStorage.getItem(frozenKey('longtasks')) || 'null');
      }
      if (!prevSlowTicks) {
        prevSlowTicks = JSON.parse(localStorage.getItem(frozenKey('slow-ticks')) || 'null');
      }
    } catch {
      /* ignore */
    }

    console.warn('[gastos-picker] tabId esta sesión:', ensureTabId(), 'frozenMeta:', frozenMeta);

    const stormToShow = prevStorm || storm;
    if (stormToShow) {
      console.warn(
        '[gastos-picker] 🎯 STACKS CAPTURADOS DURANTE LA TORMENTA (Zone.scheduleMicroTask / queueMicrotask):'
      );
      (stormToShow.stacks || []).forEach((s: string, i: number) =>
        console.warn(`--- stack #${i + 1} (${stormToShow.t}) ---\n${s}`)
      );
    } else {
      console.warn(
        '[gastos-picker] sin stacks de tormenta. Si hubo STORM, el hook no alcanzó a capturar (matar pestaña más tarde o revisar Zone).'
      );
    }

    const ltShow = (prevLongtasks as unknown[]) || (longtasks as unknown[]) || [];
    if (ltShow.length) {
      console.warn('[gastos-picker] longtasks (freeze preferido):', ltShow);
    }
    const stShow = (prevSlowTicks as unknown[]) || (slowTicks as unknown[]) || [];
    if (stShow.length) {
      console.warn('[gastos-picker] ticks lentos (durMs≥50, freeze preferido):', stShow);
    }

    console.warn('[gastos-picker] heartbeat/tick de ESTA sesión:', heartbeat, tick);
    console.warn('[gastos-picker] SESIÓN DEL FREEZE → heartbeat:', prevHeartbeat, 'tick:', prevTick);
    if (prevTick && prevTick.endMs == null) {
      console.warn(
        `[gastos-picker] ⚠️ tick #${prevTick.seq} con endMs=null (path=${prevTick.path ?? '?'} canvas=${prevTick.canvas ?? '?'}). Durante una tormenta esto es casi tautológico: la señal útil es tick.STORM.detected + stacks Zone.scheduleMicroTask.`
      );
    } else if (prevTick) {
      console.warn(
        `[gastos-picker] último tick del freeze SÍ terminó (${prevTick.durMs}ms). Comparar heartbeat frozen vs último breadcrumb.`
      );
    }
    return {
      rows,
      tabId: ensureTabId(),
      frozenMeta,
      heartbeat,
      tick,
      prevHeartbeat,
      prevTick,
      storm,
      prevStorm,
      longtasks,
      prevLongtasks,
      slowTicks,
      prevSlowTicks
    };
  };
  w.__gastosPickerClear = () => {
    pickerDebugClear();
    console.warn('[gastos-picker] cleared');
  };
}
